import { BillingFacturationNetResponse } from "@/Billing/Api/FacturationNet/Dto/Responses/FacNetBillLightResponse";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { Guid } from "@/Common/Models/Guid";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { ISendTaskOrMessagePopupModel } from "@/Communications/Models/UI/ITaskMessagePopupModel";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FreeFormImage } from "@/Patient/Api/Dto/Responses/FreeFormImage";
import { EncounterUnapprouvability } from "@/Patient/Api/Dto/Responses/MedicalNoteModel";
import { PatientFormAPSS } from "@/Patient/Api/Dto/Responses/PatientFormAPSS";
import { PendingApprovalNoteModel } from "@/Patient/Api/Dto/Responses/PendingApprovalNoteModel";
import { FacturationDTO, MedfarBillLightPreview } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { LightPrivateBill } from "@/Patient/Api/PrivateBill/Dto/LightPrivateBill";
import { MedfarBillingState } from "@/Patient/Components/MedfarBilling/MedfarBilling";
import { WorkSafeBcFormLight } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcFormLight";
import { SentFaxInfo } from "@/Patient/Models/Fax/SentFaxInfo";
import { FormDisplay } from "@/Patient/Models/Forms/FormDisplay";
import { Encounter as MyleModelsPatientEncounter, ItemBlockingDeleteAction } from "@/Patient/Models/MedicalSummary/Encounter";
import { EncounterNote } from "@/Patient/Models/MedicalSummary/EncounterNote";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { PatientPrescriptionItem } from "@/Patient/Models/Medication/PatientPrescriptionItem";
import { BaseObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileModel";
import { ObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/ObstetricalFileModel";
import { PdfFormInboxItem } from "@/Patient/Models/ShareItems/PdfFormInboxItem";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";
import { IMedfarBillingModel } from "@/Patient/Models/UI/MedfarBilling/IMedfarBillingModel";
import { MedicalNoteViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalNote";
import { PortalPopupModel } from "@/Portal/Models/PortalPopupModel";
import { PortalSyncMode } from "@/Portal/Models/PortalSyncMode";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";

type PatientContactId = "PatientContactId";
type PatientContactFacetId = "PatientContactFacetId";
type Encounter = "Encounter";
type EncounterNotes = "EncounterNotes";
type MedicalItemsAlertAndSpecialNeed = "MedicalItemsAlertAndSpecialNeed";
type MedicalItemsFamilyHistory = "MedicalItemsFamilyHistory";
type MedicalItemsImmunization = "MedicalItemsImmunization";
type MedicalItemsMedicalReaction = "MedicalItemsMedicalReaction";
type MedicalItemsMedicationAndPrescription = "MedicalItemsMedicationAndPrescription";
type MedicalItemsRiskFactor = "MedicalItemsRiskFactor";
type MedicalItemsVitalSign = "MedicalItemsVitalSign";
type MedicalItemsProblemsAndDiagnostics = "MedicalItemsProblemsAndDiagnostics";
type MedicalItemsScreeningTest = "MedicalItemsScreeningTest";
type Prescriptions = "Prescriptions";
type PrivateBills = "PrivateBills";
type PatientForms = "PatientForms";
type Requests = "Requests";
type ConsultationRequests = "ConsultationRequests";
type HandwrittenNotes = "HandwrittenNotes";
type IsMedicalSummarySectionExpanded = "IsMedicalSummarySectionExpanded";
type IsProgressNoteSectionExpanded = "IsProgressNoteSectionExpanded";
type IsFormsSectionExpanded = "IsFormsSectionExpanded";
type IsAssesmentSectionExpanded = "IsAssesmentSectionExpanded";
type IsPlanSectionExpanded = "IsPlanSectionExpanded";
type IsClinicalFormsExpanded = "IsClinicalFormsExpanded";
type ViewMode = "ViewMode";
type LeftMenuOpen = "LeftMenuOpen";
type ShowNewMedfarBillPopup = "ShowNewMedfarBillPopup";
type MedfarBillModel = "MedfarBillModel";
type MedfarBillModelInitialState = "MedfarBillModelInitialState";
type EncounterMedfarBillsLight = "EncounterMedfarBillsLight";
type EncounterMedfarBills = "EncounterMedfarBills";
type IsNotePopupOpen = "IsNotePopupOpen";
type NotePopupContext = "NotePopupContext";
type NotePopupLeft = "NotePopupLeft";
type NotePopupTop = "NotePopupTop";
type IsPrivateBillPopupOpen = "IsPrivateBillPopupOpen";
type IsPrivateBillEditPopupOpen = "IsPrivateBillEditPopupOpen";
type PrivateBillPopupModel = "PrivateBillPopupModel";
type PrivateBillBillableServices = "PrivateBillBillableServices";
type IsPrivateBillCreationWarningPopupOpen = "IsPrivateBillCreationWarningPopupOpen";
type ShouldPrintBillAfterSave = "ShouldPrintBillAfterSave";
type IsTaskMessagePopupInTaskMode = "IsTaskMessagePopupInTaskMode";
type TaskMessagePopupModel = "TaskMessagePopupModel";
type IsModificationLoading = "IsModificationLoading";

// medical note faxing
type MedicalNotePreviewPDFURL = "MedicalNotePreviewPDFURL";

// consultation request popup, printing and faxing
type ConsultationRequestPreviewPDFURL = "ConsultationRequestPreviewPDFURL";
type ConsultationRequestPreviewPDFFileName = "ConsultationRequestPreviewPDFFileName";
type IsConsultationRequestFaxPopupOpen = "IsConsultationRequestFaxPopupOpen";
type ConsultationRequestFaxModel = "ConsultationRequestFaxModel";
type ConsultationRequestFaxResponse = "ConsultationRequestFaxResponse";

// consult/exam request portal popup
type IsRequestPortalPopupOpen = "IsRequestPortalPopupOpen";

// prescription faxing and printing
type PrescriptionRequestPreviewPDFURL = "PrescriptionRequestPreviewPDFURL";

// lab request faxing and printing
type LabRequestPreviewPDFURL = "LabRequestPreviewPDFURL";
type LabRequestPreviewPDFURLFileName = "LabRequestPreviewPDFURLFileName";
type IsLabFaxPopupOpen = "IsLabFaxPopupOpen";
type LabRequestFaxResponse = "LabRequestFaxResponse";
type IsLabFaxSearchPopupOpen = "IsLabFaxSearchPopupOpen";
type IsLabFaxResponsePopupOpen = "IsLabFaxResponsePopupOpen";
type LabRequestFaxModel = "LabRequestFaxModel";

// request faxing and printing
type PrintCurrentRequest = "PrintCurrentRequest";
type SignCurrentRequest = "SignCurrentRequest";

// ---- edition
type ReasonClickableTextVisible = "ReasonClickableTextVisible";
type HistoryClickableTextVisible = "HistoryClickableTextVisible";
type ExamenClickableTextVisible = "ExamenClickableTextVisible";
type LabClickableTextVisible = "LabClickableTextVisible";
type ImagingClickableTextVisible = "ImagingClickableTextVisible";
type OtherNotesClickableTextVisible = "OtherNotesClickableTextVisible";
type FollowUpPlanClickableTextVisible = "FollowUpPlanClickableTextVisible";
type IsEditConsultationDetailsPopupVisible = "IsEditConsultationDetailsPopupVisible";

type ConcurrentEditSignedOffBy = "ConcurrentEditSignedOffBy";
type ConcurrentEditSignedOffDate = "ConcurrentEditSignedOffDate";
type ConcurrentEditShowWarningPopup = "ConcurrentEditShowWarningPopup";
type ConcurrenEditIsForMedicalItem = "ConcurrenEditIsForMedicalItem";

type AlreadySignedOffBy = "AlreadySignedOffBy";
type AlreadySignedOffDate = "AlreadySignedOffDate";
type AlreadySignedOffShowWarningPopup = "AlreadySignedOffShowWarningPopup";

type AutosaveFailed = "AutosaveFailed";
type ScrollPosition = "ScrollPosition";
type AutosaveFailedSignoffDate = "AutosaveFailedSignoffDate";
type AutosaveFailedSignoffUser = "AutosaveFailedSignoffUser";

type SpecialtyMedfarBillingPopup = "SpecialtyMedfarBillingPopup";
type IsMedfarBillingSpecialist = "IsMedfarBillingSpecialist";
type IsPrivateBillSaveLocked = "IsPrivateBillSaveLocked";

type FacturationNetBills = "FacturationNetBills";
type ExternalBillingClaims = "ExternalBillingClaims";

type ShowApprovalSupervisorPopup = "ShowApprovalSupervisorPopup";
type ShowApprovalSupervisorPopupOkButtonCallBack = "ShowApprovalSupervisorPopupOkButtonCallBack";
type ShowApprovalSupervisorPopupOkButtonLabel = "ShowApprovalSupervisorPopupOkButtonLabel";

type SupervisorName = "SupervisorName";
type PendingApprovalStatus = "PendingApprovalStatus";
type PendingApprovalNotes = "PendingApprovalNotes";
type PendingApprovalStatusInt = "PendingApprovalStatusInt";

type PatientAPSSForms = "PatientAPSSForms";

type SentFaxes = "SentFaxes";

type IsCreatingAPSSForm = "IsCreatingAPSSForm";

// REUSING BILL DATA ----------------------------------
type ShowReusingBillPopup = "ShowReusingBillPopup";
type ReusingPrivateBillsList = "ReusingPrivateBillsList";
type ShowReusingPrivateBillCriteria = "ShowReusingPrivateBillCriteria";
type ReusingPrivateBillCriteriaFrom = "ReusingPrivateBillCriteriaFrom";
type ReusingPrivateBillCriteriaTo = "ReusingPrivateBillCriteriaTo";
type ReusingPrivateBillSearchText = "ReusingPrivateBillSearchText";
type SelectedReusedPrivateBillFileName = "SelectedReusedPrivateBillFileName";
type SelectedReusedPrivateBillId = "SelectedReusedPrivateBillId";
type ShowNoBillsPopup = "ShowNoBillsPopup";
// ----------------------------------------------------

type ShowInvalidBillPopup = "ShowInvalidBillPopup";
type InvalidBillNote = "InvalidBillNote";

type EncounterIsDeletable = "EncounterIsDeletable";
type EncounterUnapprouvabilityKey = "EncounterUnapprouvability";

type ShowDeleteNoteWarningPopup = "ShowDeleteNoteWarningPopup";
type ShowUnapproveNoteWarningPopup = "ShowUnapproveNoteWarningPopup";
type ShowMedicalNoteErrorWarningPopup = "MedicalNoteErrorWarningPopup";

type MedicalItemsErrorList = "MedicalItemsErrorList";
type PermanentBlockingDeleteItems = "PermanentBlockingDeleteItems";
type TemporaryBlockingDeleteItems = "TemporaryBlockingDeleteItems";
type DeleteReasonCode = "DeleteReasonCode";
type DeleteDetails = "DeleteDetails";

type IndividualTasksLinkedItems = "IndividualTasksLinkedItems";
type AreAllTasksCompleted = "AreAllTasksCompleted";
type AreAllMessagesRead = "AreAllMessagesRead";
type IndividualMessageLinkedItems = "IndividualMessageLinkedItems";
type IndividualRecallLinkedItems = "IndividualRecallLinkedItems";
type AreAllRecallsCompleted = "AreAllRecallsCompleted";

type AttachedCommunicationsPopupOpen = "AttachedCommunicationsPopupOpen";
type AttachedCommunicationsPopupList = "AttachedCommunicationsPopupList";
type AttachedCommunicationsContext = "AttachedCommunicationsContext";

type AttachedCommunicationsPopupY = "AttachedCommunicationsPopupY";
type AttachedCommunicationsPopupX = "AttachedCommunicationsPopupX";

type PrintSignaturePreviewFileName = "PrintSignaturePreviewFileName";
type IsPrintSignaturePreviewPopupOpen = "IsPrintSignaturePreviewPopupOpen";
type IsSignaturePreviewPopupOpen = "IsSignaturePreviewPopupOpen";

type FormCountByModule = "FormsCountByModule";

type PrintSignatureObjectType = "PrintSignatureObjectType";
type PrintSignatureObjectId = "PrintSignatureObjectId";

type PortalLinkedItemModel = "PortalLinkedItemModel";
type PortalSyncPopupType = "PortalSyncPopupType";
type SelectedPortalRequestId = "SelectedPortalRequestId";
type PortalFileName = "PortalFileName";
type PortalFileUrl = "PortalFileUrl";
type UnsignedPortalFileName = "UnsignedPortalFileName";
type UnsignedPortalFileUrl = "UnsignedPortalFileUrl";
type PortalPreviewExportableFile = "PortalPreviewExportableFile";
type SyncToPortalMode = "SyncToPortalMode";
type IsPatientFormStatusStale = "IsPatientFormStatusStale";

type ObstetricalFileModelKey = "ObstetricalFileModel";
type PreviousObstetricalFiles = "PreviousObstetricalFiles";
type ShowDeleteObstetricalFileWarningPopup = "ShowDeleteObstetricalFileWarningPopup";
type HasPreviousObstetricalPdfForms = "HasPreviousObstetricalPdfForms";
type HasObstetricalFilesInOtherEncounters = "HasObstetricalFilesInOtherEncounters";
type ObstetricalFileFormDisplay = "ObstetricalFileFormDisplay";
type WorkSafeBcForms = "WorkSafeBcForms";
type IsHandWrittenNoteModified = "IsHandWrittenNoteModified";
type HandWrittenNoteVersion = "HandWrittenNoteVersion";

interface IPatientMedicalNoteModel {
    merge(...iterables: IPatientMedicalNoteModel[]): this;

    get(key: PatientContactId): Guid;
    set(key: PatientContactId, value: Guid): this;

    get(key: PatientContactFacetId): string;
    set(key: PatientContactFacetId, value: string): this;

    get(key: Encounter): MyleModelsPatientEncounter;
    set(key: Encounter, value: MyleModelsPatientEncounter): this;
    delete(key: Encounter): this;

    get(key: EncounterNotes): Immutable.List<EncounterNote>;
    set(key: EncounterNotes, value: Immutable.List<EncounterNote>): this;
    delete(key: EncounterNotes): this;

    get(key: MedicalItemsAlertAndSpecialNeed): Immutable.List<MedicalItemAlertsAndSpecialNeeds>;
    set(key: MedicalItemsAlertAndSpecialNeed, value: Immutable.List<MedicalItemAlertsAndSpecialNeeds>): this;
    delete(key: MedicalItemsAlertAndSpecialNeed): this;

    get(key: MedicalItemsFamilyHistory): Immutable.List<MedicalItemFamilyHistory>;
    set(key: MedicalItemsFamilyHistory, value: Immutable.List<MedicalItemFamilyHistory>): this;
    delete(key: MedicalItemsFamilyHistory): this;

    get(key: MedicalItemsImmunization): Immutable.List<MedicalItemImmunization>;
    set(key: MedicalItemsImmunization, value: Immutable.List<MedicalItemImmunization>): this;
    delete(key: MedicalItemsImmunization): this;

    get(key: MedicalItemsMedicalReaction): Immutable.List<MedicalItemMedicalReaction>;
    set(key: MedicalItemsMedicalReaction, value: Immutable.List<MedicalItemMedicalReaction>): this;
    delete(key: MedicalItemsMedicalReaction): this;

    get(key: MedicalItemsMedicationAndPrescription): Immutable.List<MedicalItemMedicationAndPrescription>;
    set(key: MedicalItemsMedicationAndPrescription, value: Immutable.List<MedicalItemMedicationAndPrescription>): this;
    delete(key: MedicalItemsMedicationAndPrescription): this;

    get(key: MedicalItemsRiskFactor): Immutable.List<MedicalItemRiskFactor>;
    set(key: MedicalItemsRiskFactor, value: Immutable.List<MedicalItemRiskFactor>): this;
    delete(key: MedicalItemsRiskFactor): this;

    get(key: MedicalItemsVitalSign): Immutable.List<MedicalItemVitalSign>;
    set(key: MedicalItemsVitalSign, value: Immutable.List<MedicalItemVitalSign>): this;
    delete(key: MedicalItemsVitalSign): this;

    get(key: MedicalItemsProblemsAndDiagnostics): Immutable.List<MedicalItemProblemAndCondition>;
    set(key: MedicalItemsProblemsAndDiagnostics, value: Immutable.List<MedicalItemProblemAndCondition>): this;
    delete(key: MedicalItemsProblemsAndDiagnostics): this;

    get(key: MedicalItemsScreeningTest): Immutable.List<MedicalItemScreeningTestResponse>;
    set(key: MedicalItemsScreeningTest, value: Immutable.List<MedicalItemScreeningTestResponse>): this;
    delete(key: MedicalItemsScreeningTest): this;

    get(key: Prescriptions): Immutable.List<PatientPrescriptionItem>;
    set(key: Prescriptions, value: Immutable.List<PatientPrescriptionItem>): this;
    delete(key: Prescriptions): this;

    get(key: PrivateBills): Immutable.List<LightPrivateBill>;
    set(key: PrivateBills, value: Immutable.List<LightPrivateBill>): this;
    delete(key: PrivateBills): this;

    get(key: PatientForms): Immutable.List<PdfFormInboxItem>;
    set(key: PatientForms, value: Immutable.List<PdfFormInboxItem>): this;
    delete(key: PatientForms): this;

    get(key: Requests): Immutable.List<RequestsInboxItem>;
    set(key: Requests, value: Immutable.List<RequestsInboxItem>): this;
    delete(key: Requests): this;

    get(key: ConsultationRequests): Immutable.List<ConsultationRequestsInboxItem>;
    set(key: ConsultationRequests, value: Immutable.List<ConsultationRequestsInboxItem>): this;
    delete(key: ConsultationRequests): this;

    get(key: HandwrittenNotes): Immutable.List<FreeFormImage>;
    set(key: HandwrittenNotes, value: Immutable.List<FreeFormImage>): this;
    delete(key: HandwrittenNotes): this;

    get(key: IsClinicalFormsExpanded): boolean;
    set(key: IsClinicalFormsExpanded, value: boolean): this;

    get(key: IsMedicalSummarySectionExpanded): boolean;
    set(key: IsMedicalSummarySectionExpanded, value: boolean): this;

    get(key: IsProgressNoteSectionExpanded): boolean;
    set(key: IsProgressNoteSectionExpanded, value: boolean): this;

    get(key: IsFormsSectionExpanded): boolean;
    set(key: IsFormsSectionExpanded, value: boolean): this;

    get(key: IsAssesmentSectionExpanded): boolean;
    set(key: IsAssesmentSectionExpanded, value: boolean): this;

    get(key: IsPlanSectionExpanded): boolean;
    set(key: IsPlanSectionExpanded, value: boolean): this;

    get(key: EncounterMedfarBillsLight): MedfarBillLightPreview[];
    set(key: EncounterMedfarBillsLight, value: MedfarBillLightPreview[]): this;

    get(key: FacturationNetBills): Immutable.List<BillingFacturationNetResponse>;
    set(key: FacturationNetBills, value: Immutable.List<BillingFacturationNetResponse>): this;

    get(key: ExternalBillingClaims): Immutable.List<ExternalBillingClaimDto>;
    set(key: ExternalBillingClaims, value: Immutable.List<ExternalBillingClaimDto>): this;

    get(key: EncounterMedfarBills): FacturationDTO[];
    set(key: EncounterMedfarBills, value: FacturationDTO[]): this;

    get(key: ViewMode): MedicalNoteViewMode;
    set(key: ViewMode, value: MedicalNoteViewMode): this;

    get(key: LeftMenuOpen): boolean;
    set(key: LeftMenuOpen, value: boolean): this;

    get(key: ShowNewMedfarBillPopup): boolean;
    set(key: ShowNewMedfarBillPopup, value: boolean): this;

    get(key: MedfarBillModel): IMedfarBillingModel;
    set(key: MedfarBillModel, value: IMedfarBillingModel): this;

    get(key: MedfarBillModelInitialState): MedfarBillingState;
    set(key: MedfarBillModelInitialState, value: MedfarBillingState): this;

    get(key: IsNotePopupOpen): boolean;
    set(key: IsNotePopupOpen, value: boolean): this;

    get(key: NotePopupContext): string;
    set(key: NotePopupContext, value: string): this;

    get(key: NotePopupTop): number;
    set(key: NotePopupTop, value: number): this;

    get(key: NotePopupLeft): number;
    set(key: NotePopupLeft, value: number): this;

    get(key: MedicalNotePreviewPDFURL): string;
    set(key: MedicalNotePreviewPDFURL, value: string): this;

    get(key: ConsultationRequestPreviewPDFURL): string;
    set(key: ConsultationRequestPreviewPDFURL, value: string): this;

    get(key: ConsultationRequestPreviewPDFFileName): string;
    set(key: ConsultationRequestPreviewPDFFileName, value: string): this;

    get(key: PrescriptionRequestPreviewPDFURL): string;
    set(key: PrescriptionRequestPreviewPDFURL, value: string): this;

    get(key: LabRequestPreviewPDFURL): string;
    set(key: LabRequestPreviewPDFURL, value: string): this;

    get(key: LabRequestPreviewPDFURLFileName): string;
    set(key: LabRequestPreviewPDFURLFileName, value: string): this;

    get(key: IsConsultationRequestFaxPopupOpen): boolean;
    set(key: IsConsultationRequestFaxPopupOpen, value: boolean): this;

    get(key: ConsultationRequestFaxModel): ConsultationRequestsInboxItem;
    set(key: ConsultationRequestFaxModel, value: ConsultationRequestsInboxItem): this;

    get(key: ConsultationRequestFaxResponse): GenericResponse;
    set(key: ConsultationRequestFaxResponse, value: GenericResponse): this;

    get(key: IsRequestPortalPopupOpen): boolean;
    set(key: IsRequestPortalPopupOpen, value: boolean): this;

    get(key: PrintCurrentRequest): boolean;
    set(key: PrintCurrentRequest, value: boolean): this;

    get(key: SignCurrentRequest): boolean;
    set(key: SignCurrentRequest, value: boolean): this;

    get(key: IsPrivateBillPopupOpen): boolean;
    set(key: IsPrivateBillPopupOpen, value: boolean): this;

    get(key: IsPrivateBillEditPopupOpen): boolean;
    set(key: IsPrivateBillEditPopupOpen, value: boolean): this;

    get(key: IsPrivateBillCreationWarningPopupOpen): boolean;
    set(key: IsPrivateBillCreationWarningPopupOpen, value: boolean): this;

    get(key: ShouldPrintBillAfterSave): boolean;
    set(key: ShouldPrintBillAfterSave, value: boolean): this;

    get(key: PrivateBillPopupModel): IPatientPrivateBillModel;
    set(key: PrivateBillPopupModel, value: IPatientPrivateBillModel): this;
    delete(key: PrivateBillPopupModel): this;

    get(key: PrivateBillBillableServices): Immutable.List<IBillableService>;
    set(key: PrivateBillBillableServices, value: Immutable.List<IBillableService>): this;
    delete(key: PrivateBillBillableServices): this;

    get(key: IsTaskMessagePopupInTaskMode): boolean;
    set(key: IsTaskMessagePopupInTaskMode, value: boolean): this;

    get(key: TaskMessagePopupModel): ISendTaskOrMessagePopupModel;
    set(key: TaskMessagePopupModel, value: ISendTaskOrMessagePopupModel): this;

    get(key: ReasonClickableTextVisible): boolean;
    set(key: ReasonClickableTextVisible, value: boolean): this;

    get(key: HistoryClickableTextVisible): boolean;
    set(key: HistoryClickableTextVisible, value: boolean): this;

    get(key: ExamenClickableTextVisible): boolean;
    set(key: ExamenClickableTextVisible, value: boolean): this;

    get(key: LabClickableTextVisible): boolean;
    set(key: LabClickableTextVisible, value: boolean): this;

    get(key: ImagingClickableTextVisible): boolean;
    set(key: ImagingClickableTextVisible, value: boolean): this;

    get(key: OtherNotesClickableTextVisible): boolean;
    set(key: OtherNotesClickableTextVisible, value: boolean): this;

    get(key: FollowUpPlanClickableTextVisible): boolean;
    set(key: FollowUpPlanClickableTextVisible, value: boolean): this;

    get(key: IsEditConsultationDetailsPopupVisible): boolean;
    set(key: IsEditConsultationDetailsPopupVisible, value: boolean): this;

    get(key: ConcurrentEditSignedOffBy): string;
    set(key: ConcurrentEditSignedOffBy, value: string): this;

    get(key: ConcurrentEditSignedOffDate): Date;
    set(key: ConcurrentEditSignedOffDate, value: Date): this;

    get(key: ConcurrentEditShowWarningPopup): boolean;
    set(key: ConcurrentEditShowWarningPopup, value: boolean): this;

    get(key: ConcurrenEditIsForMedicalItem): boolean;
    set(key: ConcurrenEditIsForMedicalItem, value: boolean): this;

    get(key: AlreadySignedOffBy): string;
    set(key: AlreadySignedOffBy, value: string): this;

    get(key: AlreadySignedOffDate): Date;
    set(key: AlreadySignedOffDate, value: Date): this;

    get(key: AlreadySignedOffShowWarningPopup): boolean;
    set(key: AlreadySignedOffShowWarningPopup, value: boolean): this;

    get(key: AutosaveFailed): boolean;
    set(key: AutosaveFailed, value: boolean): this;

    get(key: AutosaveFailedSignoffDate): Date;
    set(key: AutosaveFailedSignoffDate, value: Date): this;

    get(key: AutosaveFailedSignoffUser): string;
    set(key: AutosaveFailedSignoffUser, value: string): this;

    get(key: ScrollPosition): number;
    set(key: ScrollPosition, value: number): this;

    get(key: SpecialtyMedfarBillingPopup): boolean;
    set(key: SpecialtyMedfarBillingPopup, value: boolean): this;

    get(key: IsMedfarBillingSpecialist): boolean;
    set(key: IsMedfarBillingSpecialist, value: boolean): this;

    get(key: IsPrivateBillSaveLocked): boolean;
    set(key: IsPrivateBillSaveLocked, value: boolean): this;

    get(key: ShowApprovalSupervisorPopup): boolean;
    set(key: ShowApprovalSupervisorPopup, value: boolean): this;

    get(key: ShowApprovalSupervisorPopupOkButtonCallBack): (supervisorContactid: string, message: string, allowListChanges: IAllowlistModel) => void;
    set(key: ShowApprovalSupervisorPopupOkButtonCallBack, value: (supervisorContactid: string, message: string, allowListChanges: IAllowlistModel) => void): this;

    get(key: ShowApprovalSupervisorPopupOkButtonLabel): string;
    set(key: ShowApprovalSupervisorPopupOkButtonLabel, value: string): this;

    get(key: SupervisorName): string;
    set(key: SupervisorName, value: string): this;

    get(key: PendingApprovalStatus): string;
    set(key: PendingApprovalStatus, value: string): this;

    get(key: PendingApprovalNotes): Immutable.List<PendingApprovalNoteModel>;
    set(key: PendingApprovalNotes, value: Immutable.List<PendingApprovalNoteModel>): this;

    get(key: PendingApprovalStatusInt): number;
    set(key: PendingApprovalStatusInt, value: number): this;

    get(key: PatientAPSSForms): Immutable.List<PatientFormAPSS>;
    set(key: PatientAPSSForms, value: Immutable.List<PatientFormAPSS>): this;

    get(key: SentFaxes): { [key: Guid]: SentFaxInfo[] };
    set(key: SentFaxes, value: { [key: Guid]: SentFaxInfo[] }): this;

    get(key: IsCreatingAPSSForm): boolean;
    set(key: IsCreatingAPSSForm, value: boolean): this;

    get(key: ShowReusingBillPopup): boolean;
    set(key: ShowReusingBillPopup, value: boolean): this;

    get(key: ReusingPrivateBillsList): Immutable.List<LightPrivateBill>;
    set(key: ReusingPrivateBillsList, value: Immutable.List<LightPrivateBill>): this;

    get(key: ShowReusingPrivateBillCriteria): boolean;
    set(key: ShowReusingPrivateBillCriteria, value: boolean): this;

    get(key: ReusingPrivateBillCriteriaFrom): Date;
    set(key: ReusingPrivateBillCriteriaFrom, value: Date): this;

    get(key: ReusingPrivateBillCriteriaTo): Date;
    set(key: ReusingPrivateBillCriteriaTo, value: Date): this;

    get(key: ReusingPrivateBillSearchText): string;
    set(key: ReusingPrivateBillSearchText, value: string): this;

    get(key: SelectedReusedPrivateBillFileName): string;
    set(key: SelectedReusedPrivateBillFileName, value: string): this;

    get(key: SelectedReusedPrivateBillId): string;
    set(key: SelectedReusedPrivateBillId, value: string): this;

    get(key: ShowNoBillsPopup): boolean;
    set(key: ShowNoBillsPopup, value: boolean): this;

    get(key: ShowInvalidBillPopup): boolean;
    set(key: ShowInvalidBillPopup, value: boolean): this;

    get(key: InvalidBillNote): string;
    set(key: InvalidBillNote, value: string): this;

    get(key: IsLabFaxPopupOpen): boolean;
    set(key: IsLabFaxPopupOpen, value: boolean): this;

    get(key: IsLabFaxResponsePopupOpen): boolean;
    set(key: IsLabFaxResponsePopupOpen, value: boolean): this;

    get(key: IsLabFaxSearchPopupOpen): boolean;
    set(key: IsLabFaxSearchPopupOpen, value: boolean): this;

    get(key: LabRequestFaxModel): RequestsInboxItem;
    set(key: LabRequestFaxModel, value: RequestsInboxItem): this;

    get(key: LabRequestFaxResponse): GenericResponse;
    set(key: LabRequestFaxResponse, value: GenericResponse): this;

    get(key: EncounterIsDeletable): boolean;
    set(key: EncounterIsDeletable, value: boolean): this;

    get(key: EncounterUnapprouvabilityKey): EncounterUnapprouvability;
    set(key: EncounterUnapprouvabilityKey, value: EncounterUnapprouvability): this;

    get(key: ShowDeleteNoteWarningPopup): boolean;
    set(key: ShowDeleteNoteWarningPopup, value: boolean): this;

    get(key: ShowUnapproveNoteWarningPopup): boolean;
    set(key: ShowUnapproveNoteWarningPopup, value: boolean): this;

    get(key: ShowMedicalNoteErrorWarningPopup): boolean;
    set(key: ShowMedicalNoteErrorWarningPopup, value: boolean): this;

    get(key: MedicalItemsErrorList): Immutable.List<any>;
    set(key: MedicalItemsErrorList, value: Immutable.List<any>): this;

    get(key: PermanentBlockingDeleteItems): ItemBlockingDeleteAction[];
    set(key: PermanentBlockingDeleteItems, value: ItemBlockingDeleteAction[]): this;

    get(key: TemporaryBlockingDeleteItems): ItemBlockingDeleteAction[];
    set(key: TemporaryBlockingDeleteItems, value: ItemBlockingDeleteAction[]): this;

    get(key: DeleteReasonCode): string;
    set(key: DeleteReasonCode, value: string): this;

    get(key: DeleteDetails): string;
    set(key: DeleteDetails, value: string): this;

    get(key: IndividualTasksLinkedItems): AttachedCommunication[];
    set(key: IndividualTasksLinkedItems, value: AttachedCommunication[]): this;

    get(key: AreAllTasksCompleted): boolean;
    set(key: AreAllTasksCompleted, value: boolean): this;

    get(key: AreAllMessagesRead): boolean;
    set(key: AreAllMessagesRead, value: boolean): this;

    get(key: IndividualMessageLinkedItems): AttachedCommunication[];
    set(key: IndividualMessageLinkedItems, value: AttachedCommunication[]): this;

    get(key: IndividualRecallLinkedItems): AttachedCommunication[];
    set(key: IndividualRecallLinkedItems, value: AttachedCommunication[]): this;

    get(key: AreAllRecallsCompleted): boolean;
    set(key: AreAllRecallsCompleted, value: boolean): this;

    get(key: AttachedCommunicationsPopupOpen): boolean;
    set(key: AttachedCommunicationsPopupOpen, value: boolean): this;

    get(key: AttachedCommunicationsPopupList): AttachedCommunication[];
    set(key: AttachedCommunicationsPopupList, value: AttachedCommunication[]): this;

    get(key: AttachedCommunicationsContext): AttachedCommunicationType;
    set(key: AttachedCommunicationsContext, value: AttachedCommunicationType): this;

    get(key: AttachedCommunicationsPopupY): number;
    set(key: AttachedCommunicationsPopupY, value: number): this;

    get(key: AttachedCommunicationsPopupX): number;
    set(key: AttachedCommunicationsPopupX, value: number): this;

    get(key: PrintSignaturePreviewFileName): string;
    set(key: PrintSignaturePreviewFileName, value: string): this;

    get(key: IsPrintSignaturePreviewPopupOpen): boolean;
    set(key: IsPrintSignaturePreviewPopupOpen, value: boolean): this;

    get(key: IsSignaturePreviewPopupOpen): boolean;
    set(key: IsSignaturePreviewPopupOpen, value: boolean): this;

    get(key: FormCountByModule): { [moduleName: string]: number };
    set(key: FormCountByModule, value: { [moduleName: string]: number }): this;

    get(key: PrintSignatureObjectType): MyleObjectType;
    set(key: PrintSignatureObjectType, value: MyleObjectType): this;

    get(key: SelectedPortalRequestId): string;
    set(key: SelectedPortalRequestId, value: string): this;

    get(key: PortalLinkedItemModel): IPortalLinkedItemModel;
    set(key: PortalLinkedItemModel, value: IPortalLinkedItemModel): this;

    get(key: PortalSyncPopupType): MyleObjectType;
    set(key: PortalSyncPopupType, value: MyleObjectType): this;

    get(key: SelectedPortalRequestId): string;
    set(key: SelectedPortalRequestId, value: string): this;

    get(key: PortalFileUrl): string;
    set(key: PortalFileUrl, value: string): this;

    get(key: PortalFileName): string;
    set(key: PortalFileName, value: string): this;

    get(key: UnsignedPortalFileUrl): string;
    set(key: UnsignedPortalFileUrl, value: string): this;

    get(key: UnsignedPortalFileName): string;
    set(key: UnsignedPortalFileName, value: string): this;

    get(key: PrintSignatureObjectId): string;
    set(key: PrintSignatureObjectId, value: string): this;

    get(key: PortalPreviewExportableFile): ExportableFile;
    set(key: PortalPreviewExportableFile, value: ExportableFile): this;

    get(key: SyncToPortalMode): PortalSyncMode;
    set(key: SyncToPortalMode, value: PortalSyncMode): this;

    get(key: IsPatientFormStatusStale): boolean;
    set(key: IsPatientFormStatusStale, value: boolean): this;

    get(key: ObstetricalFileModelKey): ObstetricalFileModel;
    set(key: ObstetricalFileModelKey, value: ObstetricalFileModel): this;

    get(key: PreviousObstetricalFiles): BaseObstetricalFileModel[];
    set(key: PreviousObstetricalFiles, value: BaseObstetricalFileModel[]): this;

    get(key: ShowDeleteObstetricalFileWarningPopup): boolean;
    set(key: ShowDeleteObstetricalFileWarningPopup, value: boolean): this;

    get(key: HasPreviousObstetricalPdfForms): boolean;
    set(key: HasPreviousObstetricalPdfForms, value: boolean): this;

    get(key: ObstetricalFileFormDisplay): FormDisplay;
    set(key: ObstetricalFileFormDisplay, value: FormDisplay): this;

    get(key: HasObstetricalFilesInOtherEncounters): boolean;
    set(key: HasObstetricalFilesInOtherEncounters, value: boolean): this;

    get(key: WorkSafeBcForms): WorkSafeBcFormLight[];
    set(key: WorkSafeBcForms, value: WorkSafeBcFormLight[]): this;

    get(key: IsHandWrittenNoteModified): boolean;
    set(key: IsHandWrittenNoteModified, value: boolean): this;

    get(key: HandWrittenNoteVersion): string;
    set(key: HandWrittenNoteVersion, value: string): this;

    get(key: IsModificationLoading): boolean;
    set(key: IsModificationLoading, value: boolean): this;
}

function toPortalPopupModel(model: IPatientMedicalNoteModel): PortalPopupModel {
    return {
        exportableFilePreview: model.get("PortalPreviewExportableFile"),
        syncMode: model.get("SyncToPortalMode"),
        fileUrl: model.get("PortalFileUrl")
    } as PortalPopupModel;
}

export { IPatientMedicalNoteModel, toPortalPopupModel };
